import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { verifiedPnlText, forexApi, type TradePositionDTO } from '@/services/forexApi';
import ProfitCurve, { ProfitReadTime } from '@/components/feature/ProfitCurve';
import { useFollowPerformanceSummary } from '@/lib/useFollowPerformanceSummary';

type TimeRange = '7d' | '30d' | '90d' | '6mo';

const TIME_RANGE_OPTIONS: { key: TimeRange; label: string; days: number }[] = [
  { key: '7d', label: '7天', days: 7 },
  { key: '30d', label: '30天', days: 30 },
  { key: '90d', label: '90天', days: 90 },
  { key: '6mo', label: '近6个月', days: 180 },
];

function formatSymbol(symbol: string): string {
  if (!symbol) return '-';
  if (symbol.includes('/')) return symbol;
  if (symbol.length === 6) return `${symbol.slice(0, 3)}/${symbol.slice(3)}`;
  if (symbol === 'XAUUSD') return 'XAU/USD';
  return symbol;
}

function formatTime(value: string): string {
  const time = new Date(value);
  if (Number.isNaN(time.getTime())) return value;
  return time.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function ProfitStatsPage() {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const days = TIME_RANGE_OPTIONS.find(item => item.key === timeRange)?.days ?? 30;
  const { summary, readAt, loading: summaryLoading, error: summaryError, refresh: refreshSummary } = useFollowPerformanceSummary(accessToken, user?.user_id, days);
  const [executions, setExecutions] = useState<TradePositionDTO[]>([]);
  const [executionsLoading, setExecutionsLoading] = useState(true);
  const [executionsError, setExecutionsError] = useState(false);
  const [executionsRetry, setExecutionsRetry] = useState(0);

  useEffect(() => {
    setExecutions([]);
    setExecutionsError(false);
  }, [accessToken, timeRange]);

  useEffect(() => {
    if (!accessToken) { setExecutionsLoading(false); return; }
    let active = true;
    setExecutionsLoading(true);
    setExecutionsError(false);
    void forexApi.listClosedTradePositions(accessToken).then((value) => {
      if (active) setExecutions(value.items);
    }).catch(() => { if (active) setExecutionsError(true); })
      .finally(() => { if (active) setExecutionsLoading(false); });
    return () => { active = false; };
  }, [accessToken, timeRange, executionsRetry]);

  const filteredExecutions = useMemo(() => {
    const days = TIME_RANGE_OPTIONS.find((item) => item.key === timeRange)?.days || 30;
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
    return executions.filter((execution) => {
      const created = new Date(execution.closed_at || execution.opened_at).getTime();
      return Number.isNaN(created) || created >= cutoff;
    });
  }, [executions, timeRange]);

  const verified = summary?.total_pnl != null && !!summary.pnl_currency;
  const bySymbol = verified ? summary?.by_symbol ?? [] : [];
  const timeRangeLabel = TIME_RANGE_OPTIONS.find((item) => item.key === timeRange)?.label || '30天';

  return (
    <div className="min-h-screen pb-8" style={{ background: '#F7F8FC' }}>
      <div className="bg-white border-b border-gray-100" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 48px)' }}>
        <div className="flex items-center gap-3 px-5 pb-3">
          <button onClick={() => navigate('/assets')} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-gray-600 text-lg" />
            </div>
          </button>
          <h1 className="text-gray-900 font-bold text-base">AI 决策表现</h1>
        </div>
      </div>

      <div className="px-5 flex flex-col gap-3">
        <div className="flex items-center gap-1.5 text-gray-400 text-[10px]">
          <div className="w-3 h-3 flex items-center justify-center">
            <i className="ri-information-line text-[10px]" />
          </div>
          仅展示最近 {summary?.max_recent_order_query_days || 180} 天下单数据
        </div>

        <div className="flex gap-2">
          {TIME_RANGE_OPTIONS.map((range) => (
            <button
              key={range.key}
              onClick={() => setTimeRange(range.key)}
              className="flex-1 py-2 rounded-xl text-xs font-medium transition-all"
              style={timeRange === range.key
                ? { background: '#6366F1', color: '#fff' }
                : { background: '#fff', color: '#6B7280', border: '1px solid #E5E7EB' }}
            >
              {range.label}
            </button>
          ))}
        </div>

        <>
          {summaryLoading && !summary && <p role="status" className="text-xs text-gray-500">正在加载收益…</p>}
          {summaryError && <p role="alert" className="text-xs text-amber-700">{summary ? '收益更新失败，显示上次结果。' : '收益加载失败。'} <button onClick={refreshSummary}>重试收益</button></p>}
          {!summary && <ProfitCurve summary={null} loading={summaryLoading} error={summaryError} />}
          {summary && <>
            <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-robot-2-line text-indigo-500 text-sm" />
                </div>
                <h2 className="text-gray-900 font-bold text-sm">AI 决策表现概览</h2>
                <span className="ml-auto text-gray-400 text-[10px]">{timeRangeLabel}</span>
                <button type="button" aria-label="刷新收益" disabled={summaryLoading} onClick={refreshSummary} className="min-h-11 px-2 text-xs text-indigo-600 disabled:opacity-50">{summaryLoading ? '读取中…' : '刷新'}</button>
              </div>

              <ProfitCurve summary={summary} loading={summaryLoading} error={summaryError} />
              <ProfitReadTime readAt={readAt} stale={summaryError} />
              {!verified && <p className="mb-3 text-xs text-amber-700">该周期的平仓盈亏或账户币种尚未核对，统计指标暂不可用。</p>}

              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  { label: `${timeRangeLabel}已实现盈亏`, value: verifiedPnlText(summary?.total_pnl, summary?.pnl_currency), color: '#10B981' },
                  { label: '已核对平仓', value: `${summary?.closed_trade_count ?? 0} 笔`, color: '#8B5CF6' },
                  { label: '胜率', value: verified && summary?.win_rate != null ? `${summary.win_rate.toFixed(1)}%` : '待核对', color: '#10B981' },
                  { label: '最大单笔亏损', value: verifiedPnlText(summary?.max_single_loss, summary?.pnl_currency), color: '#EF4444' },
                  { label: '运行跟单', value: `${summary?.active_follow_relations || 0} 个`, color: '#6366F1' },
                  { label: '总跟单关系', value: `${summary?.total_follow_relations || 0} 个`, color: '#6366F1' },
                ].map((metric) => (
                  <div key={metric.label} className="text-center bg-gray-50 rounded-xl py-2">
                    <p className="font-bold text-sm" style={{ color: metric.color }}>{metric.value}</p>
                    <p className="text-gray-400 text-[10px] mt-0.5">{metric.label}</p>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 border border-amber-100 rounded-xl p-2.5 flex gap-2">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-amber-500 text-xs" />
                </div>
                <p className="text-amber-700 text-[10px] leading-relaxed">
                  历史表现不预示未来结果。AI 跟单不保证收益，过往胜率和盈亏不代表未来交易结果。
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-pie-chart-2-line text-indigo-500 text-sm" />
                </div>
                <h2 className="text-gray-900 font-bold text-sm">按品种表现归因</h2>
              </div>

              {bySymbol.length === 0 ? (
                <div className="py-6 text-center text-gray-400 text-xs">暂无可核对的平仓表现</div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {bySymbol.map((item) => (
                    <div key={item.symbol} className="flex items-center gap-3">
                      <div className="w-20 flex-shrink-0">
                        <p className="text-gray-900 font-medium text-xs truncate">{item.symbol}</p>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex-1">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${Math.min(Math.abs(item.pnl), 100)}%`,
                                background: item.pnl >= 0 ? '#10B981' : '#EF4444',
                                opacity: 0.7,
                              }}
                            />
                          </div>
                          <span className={`text-[10px] font-semibold w-20 text-right ${item.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {verifiedPnlText(item.pnl, summary?.pnl_currency)}
                          </span>
                        </div>
                        <span className="text-[9px] text-gray-400">
                          {item.trades} 笔 · 胜率 {item.trades > 0 ? ((item.wins / item.trades) * 100).toFixed(1) : '0.0'}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </>}
            <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-file-list-3-line text-indigo-500 text-sm" />
                  </div>
                  <h2 className="text-gray-900 font-bold text-sm">本人 Broker 平仓记录</h2>
                </div>
                <span className="text-gray-400 text-[10px]">{(executionsLoading || executionsError) && executions.length === 0 ? '—' : `${filteredExecutions.length} 条`}</span>
              </div>

              {executionsLoading && executions.length === 0 && <p role="status" className="text-xs text-gray-500">正在加载平仓记录…</p>}
              {executionsError && <p role="alert" className="text-xs text-amber-700">{executions.length ? '平仓记录更新失败，显示上次结果。' : '平仓记录加载失败。'} <button onClick={() => setExecutionsRetry((key) => key + 1)}>重试平仓记录</button></p>}
              {!executionsLoading && !executionsError && filteredExecutions.length === 0 && <p className="text-xs text-gray-500">暂无已平仓记录</p>}
              <div className="flex flex-col gap-2">
                {filteredExecutions.slice(0, 20).map((execution) => (
                  <div
                    key={execution.id}
                    className="bg-gray-50 rounded-xl p-3 cursor-pointer active:scale-[0.98] transition-transform"
                    onClick={() => navigate('/trade-history')}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                          style={{
                            background: execution.action === 'BUY' ? '#ECFDF5' : '#FEF2F2',
                            color: execution.action === 'BUY' ? '#10B981' : '#EF4444',
                          }}
                        >
                          {execution.action}
                        </span>
                        <span className="text-gray-900 font-semibold text-sm">{formatSymbol(execution.symbol)}</span>
                        <span className="text-gray-300 text-[10px]">{typeof execution.lot === 'number' ? `${execution.lot} lot` : '手数待回写'}</span>
                      </div>
                      <span className={`text-sm font-bold ${typeof execution.pnl !== 'number' ? 'text-gray-400' : execution.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {verifiedPnlText(execution.pnl, execution.pnl_currency)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-500 text-[10px]">Broker 已平仓</span>
                        <span className="text-gray-300 text-[10px]">·</span>
                        <span className="text-gray-400 text-[10px]">{formatTime(execution.closed_at || execution.opened_at)}</span>
                      </div>
                      <span className="text-indigo-500 text-[10px] font-medium flex items-center gap-0.5">
                        查看详情
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-arrow-right-s-line text-[10px]" />
                        </div>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
        </>
      </div>
    </div>
  );
}
